// hooks/useBusinessInfo.ts
import { useState, useEffect } from "react";
import { OrganizationDetails, BusinessPrintData } from "./types";
import api from "../../../../../features/auth/axiosConfig";

// ✅ Helper to parse JSON from sessionStorage safely
const getSessionValue = <T = string>(key: string, fallback: T): T => {
  const raw = sessionStorage.getItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return raw as unknown as T; // fallback to string if not JSON
  }
};

export const useBusinessInfo = () => {
  const [organizationData, setOrganizationData] = useState<OrganizationDetails[]>([]);
  const [businessPrint, setBusinessPrint] = useState<BusinessPrintData>({
    logo: "",
    phone: "",
    name: "",
    addressline: "",
    arabicName: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBusinessData = async () => {
      try {
        const response = await api.get("/pos/v2/get_user");
        setOrganizationData(response.data.result);

        // Read from sessionStorage
        const businessData = getSessionValue<Partial<BusinessPrintData>>("business_type", {});
        const name = sessionStorage.getItem("organization_name") || "";
        const arabicName = sessionStorage.getItem("businessArabicName") || "";
        const addressline = sessionStorage.getItem("address") || "";
        const phone = sessionStorage.getItem("business_phone") || "";
        const logo = sessionStorage.getItem(`Logo${name}`) || "";

        // Merge & update state
        setBusinessPrint({
          logo,
          phone: phone || businessData.phone || "",
          name,
          addressline: addressline || businessData.addressline || "",
          arabicName,
        });
      } catch (err) {
        console.error("Error fetching business data:", err);
        setError("Failed to load business info");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBusinessData();
  }, []);

  return {
    organizationData,
    businessPrint,
    isLoading,
    error,
  };
};
